import React, { useCallback, useEffect, useState } from 'react';

import { FormControl, TextField, Autocomplete } from '@mui/material';
import classNames from 'classnames';

import { useDebounce } from '../../hooks';
import { Icon } from '../../Icon';
import { Label, Preloader } from '../common';
import { useAutocompleteReducer } from '../hooks';
import { Filter } from '../types';

import useStyles from './SuggestedInput.styles';
import { SuggestedInputProps } from './SuggestedInput.types';
import { ListBox } from './ListBox';
import { TextFieldMasked } from './TextFieldMasked';

const DEBOUNCE_TIMEOUT = 200;

/**
 * SuggestedInput — компонент, который предоставляет поле ввода с подсказками.
 * Он поддерживает локальные и загружаемые подсказки, настраиваемое отображение вариантов и всплывающие подсказки.
 *
 * @param {Object} props - Свойства компонента.
 * @param {string} props.name - Атрибут name для поля ввода.
 * @param {string} props.label - Текст метки для поля ввода.
 * @param {string | null} props.value - Текущее значение поля ввода.
 * @param {(value: string | undefined) => void} props.onChange - Функция обратного вызова для обработки изменений значения ввода.
 * @param {() => void} [props.onBlur] - Функция обратного вызова для обработки события потери фокуса.
 * @param {string} [props.placeholder] - Текст плейсхолдера для поля ввода.
 * @param {boolean} [props.disabled=false] - Если true, поле ввода будет отключено.
 * @param {boolean} [props.required=false] - Если true, поле ввода будет отмечено как обязательное.
 * @param {boolean} [props.clearable=false] - Если true, поле ввода будет иметь кнопку очистки.
 * @param {boolean} [props.loading=false] - Если true, поле ввода будет показывать индикатор загрузки.
 * @param {ReactNode | null} [props.tooltip=null] - Содержимое всплывающей подсказки, отображаемой с меткой.
 * @param {boolean} [props.error=false] - Если true, поле ввода будет отмечено как содержащее ошибку.
 * @param {string | ReactNode} [props.helperText] - Вспомогательный текст, отображаемый с полем ввода.
 * @param {(option: string) => React.ReactNode} [props.renderOption] - Пользовательская функция отображения вариантов.
 * @param {number} [props.limit=50] - Максимальное количество элементов для запроса.
 * @param {number} [props.minInputValueLength=1] - Минимальная длина ввода, необходимая для вызова подсказок.
 * @param {string} [props.mask] - Маска, применяемая к полю ввода.
 * @param {'local' | 'load'} props.type - Тип подсказок ('local' или 'load').
 * @param {(filter: Filter, mode: 'get' | 'search') => Promise<void>} [props.loadItems] - Функция для загрузки элементов на основе фильтра.
 * @param {string[]} [props.externalItems] - Внешний список элементов, используемых для подсказок.
 * @returns {JSX.Element} - JSX компонента.
 */

export const SuggestedInput = (props: SuggestedInputProps) => {
  const {
    name,
    label,
    value,
    onChange,
    onBlur,
    placeholder,
    disabled = false,
    required = false,
    clearable = false,
    loading = false,
    tooltip,
    error = false,
    helperText,
    renderOption,
    limit = 50,
    minInputValueLength = 1,
    mask,
  } = props;

  const { classes } = useStyles();

  const { items, status, hasMore, requestItems, resetItems } =
    useAutocompleteReducer<string>(
      props.type === 'load' ? props.loadItems : undefined,
    );

  const [isLoading, setLoading] = useState(false);
  const [filter, setFilter] = useState<Filter>({
    limit,
    offset: 0,
    query: '',
  });
  const [open, setOpen] = useState(false);

  const isItemsLoading = isLoading || status === 'loading';
  const debouncedInputValue = useDebounce(value, DEBOUNCE_TIMEOUT) ?? '';

  const handleChange = useCallback(
    (_: React.SyntheticEvent, nextValue: string | null) => {
      onChange(nextValue ?? undefined);

      if (props.type === 'load') {
        resetItems();
        setFilter({ ...filter, offset: 0, query: '' });
      }

      setOpen(false);
    },
    [onChange, filter, props.type, resetItems],
  );

  const handleInputChange = useCallback(
    (_: React.SyntheticEvent, nextValue: string | null, reason: string) => {
      if (reason === 'reset') return;

      onChange(nextValue ?? undefined);

      if (nextValue?.replace(/_+/g, '').trim()) {
        if (!open) {
          setOpen(true);
        }
      } else {
        setOpen(false);
      }
    },
    [onChange, open],
  );

  const handleBlur = useCallback(() => {
    onBlur?.();
    if (open) setOpen(false);
  }, [onBlur, open]);

  const handleOpen = useCallback(() => {
    if (value?.replace(/_+/g, '').trim()) {
      setOpen(true);
    }
  }, [value]);

  const requestNextItems = () => {
    requestItems?.(filter, 'get');
    setFilter({
      ...filter,
      offset: filter.offset + filter.limit,
    });
  };

  useEffect(() => {
    if (
      props.type === 'local' ||
      !open ||
      (debouncedInputValue !== '' &&
        debouncedInputValue?.length < minInputValueLength)
    ) {
      return;
    }

    const abortRequest = requestItems?.(
      {
        ...filter,
        offset: 0,
        query: debouncedInputValue,
      },
      'search',
    );
    setFilter({
      ...filter,
      offset: filter.limit,
    });
    setLoading(false);

    return () => {
      abortRequest?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedInputValue]);

  const filterOptions = (
    optionList: string[],
    state: { inputValue: string },
  ) => {
    const cleanInput = state.inputValue.replace(/_/g, '').trim();

    return optionList.filter((optionElement: string) => {
      return optionElement.toLowerCase().includes(cleanInput.toLowerCase());
    });
  };

  return (
    <FormControl
      className={classNames(classes.suggestedInput, 'aqa_suggested_input')}
    >
      {label && <Label label={label} required={required} tooltip={tooltip} />}

      <Autocomplete
        classes={{
          paper: classes.paper,
          option: classes.option,
        }}
        options={props.type === 'load' ? items : props.externalItems}
        filterOptions={filterOptions}
        fullWidth
        freeSolo
        renderTags={() => null}
        disabled={disabled || loading}
        disableClearable={!clearable}
        clearIcon={
          <Icon name="cancel_circle" color="stroke.secondary" size="20" />
        }
        inputValue={loading ? 'Загрузка...' : value}
        onInputChange={mask ? undefined : handleInputChange}
        onChange={handleChange}
        open={open}
        onOpen={handleOpen}
        onClose={handleBlur}
        renderOption={(renderOptionProps, option) => (
          <li {...renderOptionProps}>
            {renderOption ? renderOption(option) : <div>{option}</div>}
          </li>
        )}
        slotProps={{
          listbox: {
            component: ListBox,
            ...{
              dataLength: items.length,
              next: requestNextItems,
              hasMore,
              loading: isItemsLoading,
              long: !!renderOption,
            },
          },
        }}
        renderInput={(params) => {
          return mask ? (
            <TextFieldMasked
              {...params}
              mask={mask}
              name={name}
              error={error}
              helperText={helperText}
              placeholder={placeholder || label}
              loading={loading}
              onChange={handleInputChange as never}
            />
          ) : (
            <TextField
              {...params}
              name={name}
              classes={{ root: classes.inputRoot }}
              autoComplete="off"
              variant="outlined"
              fullWidth
              error={error}
              helperText={helperText}
              placeholder={placeholder || label}
              {...(loading
                ? {
                    slotProps: {
                      input: {
                        endAdornment: <Preloader />,
                      },
                    },
                  }
                : {})}
            />
          );
        }}
      />
    </FormControl>
  );
};
